import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { Router } from '@angular/router';
import { features } from '@banyan/commons/consts/feature-toggling.const';
import { FALLBACK_LOCAL, supportedLocales } from '@banyan/commons/consts/locale.const';
import { LOCALE } from '@banyan/commons/enum/locale';
import { Profile } from '@banyan/commons/models/profile.model';
import { ProfileModify } from '@banyan/commons/models/profile-update.model';
import { User } from '@banyan/commons/models/user.model';
import { FeatureTogglingService } from '@banyan/commons/services/feature-toggling.service';
import { ProgressSpinnerService } from '@banyan/commons/services/progress-spinner.service';
import { CaptureNeedService } from '@banyan/features/capture-needs/services/capture-need.service';
import { CartV2Service } from '@banyan/features/cart/services/cart-v2.service';
import { environment } from '@env/environment';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { OAuthService } from 'angular-oauth2-oidc';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly gtmService = inject(GoogleTagManagerService);
  private readonly featureTogglingService = inject(FeatureTogglingService);

  httpClient = inject(HttpClient);
  oAuthService = inject(OAuthService);
  router = inject(Router);
  progressSpinnerService = inject(ProgressSpinnerService);
  captureNeedService = inject(CaptureNeedService);
  cartV2Service = inject(CartV2Service);
  userLoaded = signal<boolean>(false);
  private readonly PATH: string = '/account';
  user = signal<User>({
    isAuthenticated: false,
    profile: null,
    preferredCurrency: 'IDR',
    preferredLanguage: LOCALE.ID,
    utm: {
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_id: '',
      utm_content: '',
    },
    isAdmin: false,
    profileBc: null,
    volumeUnit: 'Kg',
  });

  constructor() {
    if (this.featureTogglingService.isFeatureAllowed('EN', features)) {
      this.user.update(x => ({ ...x, preferredLanguage: LOCALE.EN }));
    }
  }

  initAuth(url: string = '') {
    if (isPlatformBrowser(this.platformId)) {
      this.oAuthService.initCodeFlow(url);
    }
  }

  checkLoginStatus() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.oAuthService.hasValidAccessToken()) {
        this.progressSpinnerService.show();
        this.loadProfileBc();
        this.loadProfile();
      }
    }
  }

  tryLogin() {
    if (isPlatformBrowser(this.platformId)) {
      this.oAuthService.tryLogin().then(value => {
        if (value) {
          this.progressSpinnerService.show();
          this.loadProfileBc();
          this.loadProfile();
        }
      }).catch(() => {
        this.progressSpinnerService.hide();
        this.router.navigate(['/']).then();
      });
    }
  }

  checkUserAdmin() {
    const hasCognitoGroups = !!(this.user()?.profile?.info['cognito:groups']);
    if (hasCognitoGroups) {
      const isAdmin = this.user()?.profile?.info['cognito:groups'].includes('Admin');
      this.user.update(x => ({ ...x, isAdmin }));
    }
  }

  loadProfile() {
    this.oAuthService.loadUserProfile().then(profile => {
      this.user.update(x => ({ ...x, profile, isAuthenticated: true }));
      this.checkUserAdmin();
      this.cartV2Service.getCart();
      if (this.featureTogglingService.isFeatureAllowed('EN', features)) {
        this.user.update(x => ({ ...x, preferredLanguage: LOCALE.EN }));
      }
      this.redirectUrl();
    }).catch(() => {
      this.user.update(user => ({ ...user, profile: null, isAuthenticated: false }));
      this.progressSpinnerService.hide();
      this.router.navigate(['/']).then();
    });
  }

  loadProfileBc() {
    this.httpClient
      .get<Profile>(`${environment.middlewareUrl}${this.PATH}`)
      .pipe(
        map(profile => {
          return {
            ...profile,
            preferredLanguage: profile.preferredLanguage ?? FALLBACK_LOCAL,
          };
        }),
      )
      .subscribe({
        next: value => {
          const locale = supportedLocales.find(locale => locale === value.preferredLanguage) ?? FALLBACK_LOCAL;
          this.user.update(user => ({ ...user, profileBc: value, preferredLanguage: locale }));
          this.captureNeedService.languageChanged.set(true);
          this.gtmService.pushTag({
            event: 'userAuthenticated',
            userId: value.id,
            userName: value.firstName + ' ' + value.lastName,
            userEmail: value.email,
          }).then();
          this.userLoaded.set(true);
          this.progressSpinnerService.hide();
        }, error: () => {
          this.user.update(user => ({ ...user, profileBc: null }));
          this.progressSpinnerService.hide();
        },
      });
  }

  redirectUrl() {
    if (isPlatformBrowser(this.platformId)) {
      const urlStorage = window.localStorage.getItem('Url');
      if (urlStorage) {
        window.localStorage.removeItem('Url');
        this.router.navigate([urlStorage]).then();
      }
    }
  }

  setInitLocale() {
    this.setLocale(FALLBACK_LOCAL);
  }

  setLocale(locale: LOCALE) {
    this.user.update(user => ({ ...user, preferredLanguage: locale }));
    if (isPlatformBrowser(this.platformId)) {
      if (this.oAuthService.hasValidAccessToken() && this.user().profile) {
        const profilePartial = {
          preferredLanguage: this.user().preferredLanguage ?? '',
        };
        this.updateProfileBc(profilePartial);
      }
    }
  }

  redirectUnauthorized() {
    this.logout();
  }

  updateProfileBc(profile: Partial<ProfileModify>) {
    this.progressSpinnerService.show();
    this.httpClient.put<void>(
      `${environment.middlewareUrl}${this.PATH}`,
      profile,
    ).subscribe({
      next: () => {
        if (profile.firstName) {
          this.user.update(user => ({
            ...user, profileBc: user.profileBc
              ? {
                ...user.profileBc,
                firstName: profile.firstName ?? '',
                lastName: profile.lastName ?? '',
                phone: profile.phone ?? '',
              }
              : null,
          }));
        }
        this.progressSpinnerService.hide();
      }, error: () => {
        this.progressSpinnerService.hide();
      },
    });
  }

  deleteProfile(): Observable<void> {
    return this.httpClient.delete<void>(
      `${environment.middlewareUrl}${this.PATH}`,
    );
  }

  logout() {
    if (isPlatformBrowser(this.platformId)) {
      this.oAuthService.logOut({
        client_id: environment.auth.clientId,
        logout_uri: environment.auth.postLogoutRedirectUri,
      });
      window.localStorage.setItem('Url', '/');
      this.router.navigate(['/']).then();
    }
  }
}
